import Decimal from "decimal.js";
import isEqual from "fast-deep-equal";
import {
  convertQuoteAmountToBase,
  type HydratedQuantityBreak,
  type Item,
  type ItemUnit,
  type WeightUnit,
} from "@forgepoint/shared";
import type { QuoteCurrencyFields } from "@/lib/quoteCurrency";
import type { QuoteEditorData } from "./QuoteEditor";

/**
 * Converts a document-currency value (a manual override or AMOUNT discount,
 * stored in the quote's display currency) to its base-currency equivalent at
 * the pinned snapshot, tolerating mid-edit input.
 * @param value - the value in the quote's display currency
 * @param currencyContext - the quote's currency context
 * @returns the base equivalent as a decimal string, or null when not numeric
 */
const toBaseEquivalent = (
  value: string,
  currencyContext?: QuoteCurrencyFields,
): string | null => {
  if (value === "" || Number.isNaN(Number(value))) {
    return null;
  }
  return convertQuoteAmountToBase(value, currencyContext);
};

/**
 * Calculates the base-currency line total for a quantity break using decimal
 * arithmetic. A manual override takes priority and is denominated in the
 * quote's display currency, so it is first derived to base at the pinned
 * snapshot; otherwise the server-calculated base unit price applies. Returns
 * null when no valid numeric inputs are found.
 * @param overrideValue - The document-currency override value (takes priority)
 * @param quantity - The quantity value
 * @param hydratedUnitPrice - The server-calculated base unit price (fallback)
 * @param currencyContext - The quote's currency context for the override
 * @returns The base line total as a string with two decimals, or null if no
 * valid price
 */
export const calculateLineTotal = (
  overrideValue: string | undefined,
  quantity: string | undefined,
  hydratedUnitPrice?: string | null,
  currencyContext?: QuoteCurrencyFields,
): string | null => {
  const priceToUse =
    overrideValue !== undefined
      ? toBaseEquivalent(overrideValue, currencyContext)
      : hydratedUnitPrice;
  if (!priceToUse) {
    return null;
  }
  try {
    const qty = new Decimal(quantity ?? "0");
    const price = new Decimal(priceToUse);
    return qty.mul(price).toFixed(2);
  } catch {
    return null;
  }
};

/**
 * Margin of a quantity break in base currency: profit per unit (sold unit
 * price − raw unit cost) and margin % (profit ÷ sold price, one decimal). The
 * sold price is the override (converted to base) when set, else the hydrated
 * unit price. Null when price or cost is missing, non-numeric, or not positive.
 */
export const lineMargin = (
  overrideValue: string | undefined,
  hydratedUnitPrice?: string | null,
  hydratedUnitCost?: string | null,
  currencyContext?: QuoteCurrencyFields,
): { percent: string; profitPerUnit: string } | null => {
  const soldPrice =
    overrideValue !== undefined
      ? toBaseEquivalent(overrideValue, currencyContext)
      : hydratedUnitPrice;
  if (!soldPrice || !hydratedUnitCost) {
    return null;
  }
  const price = new Decimal(soldPrice);
  const cost = new Decimal(hydratedUnitCost);
  if (price.lte(0) || cost.lte(0)) {
    return null;
  }
  const profit = price.minus(cost);
  return {
    percent: profit.div(price).times(100).toFixed(1),
    profitPerUnit: profit.toString(),
  };
};

/**
 * Unit price divided by the item's weight per unit, or null when either is missing.
 * Rounded to `decimals` when set; otherwise the unit-price formatter caps the digits.
 */
export function pricePerWeightUnit(
  unitPrice: string | null | undefined,
  item: Pick<Item, "weight" | "weightUnit"> | null | undefined,
  decimals: number | undefined,
): { value: string; unit: WeightUnit } | null {
  if (!unitPrice || !item?.weight || !item.weightUnit) {
    return null;
  }
  const weight = new Decimal(item.weight);
  if (weight.lte(0)) {
    return null;
  }
  const perWeight = new Decimal(unitPrice).div(weight);
  return {
    value: (decimals === undefined
      ? perWeight
      : perWeight.toDecimalPlaces(decimals)
    ).toString(),
    unit: item.weightUnit,
  };
}

/**
 * The weight of one selling unit. Only EACH uses the stored per-each weight;
 * other units divide the break's total weight by its quantity, or return null
 * when the break has no weight.
 * @param hydratedBreak - The line's hydrated quantity break
 * @param item - The item as sold on the line
 * @param lineUnit - The unit the line is quoted in
 * @returns The weight of one selling unit and its unit, or null when unknown
 */
export function lineUnitWeight(
  hydratedBreak: HydratedQuantityBreak | undefined,
  item: Pick<Item, "unit" | "weight" | "weightUnit"> | null | undefined,
  lineUnit: ItemUnit | null,
): Pick<Item, "weight" | "weightUnit"> | null {
  if (!item || lineUnit === "each") {
    return item ?? null;
  }
  if (!hydratedBreak?.totalWeight) {
    return null;
  }
  const quantity = new Decimal(hydratedBreak.quantity);
  if (quantity.lte(0)) {
    return null;
  }
  return {
    weight: new Decimal(hydratedBreak.totalWeight).div(quantity).toString(),
    weightUnit: hydratedBreak.totalWeightUnit ?? item.weightUnit ?? null,
  };
}

/**
 * Whether a save must send the editor's lines: they differ from the server's,
 * or the currency changed (the editor re-denominates lines on a currency change).
 * @param data - The editor data being saved
 * @param saved - The editor data of the quote as the server last returned it
 * @returns True when the save must include the lines
 */
export function hasUnsavedLines(
  data: Pick<QuoteEditorData, "lines" | "quoteCurrency">,
  saved: Pick<QuoteEditorData, "lines" | "quoteCurrency"> | null,
): boolean {
  if (!saved || data.quoteCurrency !== saved.quoteCurrency) {
    return true;
  }
  return !isEqual(data.lines, saved.lines);
}
